/**
 * ============================================================================
 *   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * ============================================================================
 *   Module       : useScreenRefresh
 *   Feature      : 226-universal-page-affordances (owner decision 10, FR-011)
 *   Description  : The header Refresh for a screen made of panels that each fetch
 *                  for themselves (the role dashboards): re-fetches every query on
 *                  screen. Rejects when one fails, and keeps that failure for the
 *                  Refresh button's badge until a refresh succeeds; the panels keep
 *                  the data they already hold. The spinner covers this refresh only,
 *                  not the panels' own polling.
 *   Author       : Peter Dobler
 *   Created      : 2026-10-29
 * ============================================================================
 */
import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

export interface ScreenRefresh {
  refresh: () => Promise<void>;
  isRefreshing: boolean;
  /** The last header refresh's failure; null once one succeeds. */
  error: unknown;
}

export function useScreenRefresh(): ScreenRefresh {
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const refresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      // type 'active' = queries with a mounted observer: what the screen is showing.
      await queryClient.refetchQueries({ type: 'active' }, { throwOnError: true });
      setError(null);
    } catch (e) {
      setError(e);
      throw e;
    } finally {
      setIsRefreshing(false);
    }
  }, [queryClient]);

  return { refresh, isRefreshing, error };
}

export default useScreenRefresh;
